import { useId, useState, type ReactNode } from 'react';

import { cn } from '@mds/shared/lib/utils';

/** Longest bar, as a percentage of the row — the rest is the tip label's room. */
const BAR_MAX_PCT = 88;

/** Floor width so a zero-ish value still shows a mark rather than nothing. */
const BAR_MIN_PCT = 0.6;

export interface BarRow {
  key: string;
  label: string;
  /** The plotted measure. */
  value: number;
  /** Rendered at the bar tip and in the tooltip. */
  display: string;
  /** Extra lines for the tooltip, e.g. bookings alongside revenue. */
  detail?: string[];
}

/**
 * Ranked horizontal bars for a single measure.
 *
 * One measure means one colour for every bar — shading by magnitude would
 * re-encode the bar length as hue and burn the only free channel. Two measures
 * (revenue and bookings) are two of these, never one chart with two axes.
 *
 * Bars are capped at 88% of the row so the tip label always has room; the label
 * sits immediately after the bar end and can never be clipped by its own mark.
 *
 * With `onSelect`, each row is a button: the page uses it to narrow every chart
 * to the space that was clicked.
 */
export default function BarRanking({
  rows,
  emptyHint = 'Nothing in this range.',
  onSelect,
}: {
  rows: BarRow[];
  emptyHint?: string;
  /** Makes each row clickable — for a ranking that can filter the page. */
  onSelect?: (row: BarRow) => void;
}) {
  const [active, setActive] = useState<string | null>(null);
  const id = useId();

  if (rows.length === 0) {
    return <p className="m-0 text-base text-ink-muted">{emptyHint}</p>;
  }

  const max = Math.max(...rows.map((r) => r.value), 1);
  const total = rows.reduce((n, r) => n + r.value, 0);

  return (
    <ul className="m-0 flex list-none flex-col gap-3 p-0">
      {rows.map((row) => {
        const pct = (row.value / max) * BAR_MAX_PCT;
        const share = total ? Math.round((row.value / total) * 100) : 0;
        const open = active === row.key;
        return (
          <li key={row.key} className="relative">
            <RowShell
              onSelect={onSelect ? () => onSelect(row) : undefined}
              label={onSelect ? `Show only ${row.label}` : undefined}
              describedBy={open ? `${id}-tip` : undefined}
              onEnter={() => setActive(row.key)}
              onLeave={() => setActive(null)}
            >
              <div className="flex items-baseline justify-between gap-3">
                <span className="text-base font-semibold">{row.label}</span>
                <span className="font-mono text-sm text-ink-muted tabular-nums">{share}%</span>
              </div>

              <div className="flex items-center gap-2">
                {/* Rounded data-end, square at the baseline it grows from. */}
                <div
                  className="h-4 min-w-[3px] rounded-r-sm bg-chart-mark"
                  style={{ width: `${Math.max(pct, BAR_MIN_PCT)}%` }}
                />
                <span className="shrink-0 text-base font-semibold text-ink tabular-nums">
                  {row.display}
                </span>
              </div>
            </RowShell>

            {open && (
              <div
                id={`${id}-tip`}
                role="tooltip"
                className="pointer-events-none absolute top-full left-0 z-(--z-base) mt-1 rounded-xl border border-hairline bg-surface px-4 py-3 shadow-lift"
              >
                <div className="text-base font-semibold">{row.label}</div>
                <div className="mt-1 flex flex-col gap-0.5 text-md text-ink-secondary">
                  <span className="tabular-nums">{row.display}</span>
                  {row.detail?.map((d) => (
                    <span key={d} className="tabular-nums">
                      {d}
                    </span>
                  ))}
                  <span className="text-ink-muted tabular-nums">{share}% of total</span>
                </div>
              </div>
            )}
          </li>
        );
      })}
    </ul>
  );
}

const rowShellCls =
  '-mx-2 flex w-[calc(100%+1rem)] flex-col gap-1.5 rounded-lg px-2 py-1.5 text-left focus-visible:outline-(length:--focus-ring-width) focus-visible:outline-offset-(--focus-ring-offset) focus-visible:outline-(color:--focus-ring-color)';

/**
 * One row's hit area: a button when the ranking filters the page, otherwise a
 * focusable block so the tooltip is reachable from the keyboard either way.
 */
function RowShell({
  onSelect,
  label,
  describedBy,
  onEnter,
  onLeave,
  children,
}: {
  onSelect?: () => void;
  label?: string;
  describedBy?: string;
  onEnter: () => void;
  onLeave: () => void;
  children: ReactNode;
}) {
  const shared = {
    'aria-describedby': describedBy,
    onMouseEnter: onEnter,
    onMouseLeave: onLeave,
    onFocus: onEnter,
    onBlur: onLeave,
  };
  if (onSelect) {
    return (
      <button
        type="button"
        onClick={onSelect}
        aria-label={label}
        className={cn(rowShellCls, 'cursor-pointer bg-transparent hover:bg-brand-wash')}
        {...shared}
      >
        {children}
      </button>
    );
  }
  return (
    <div tabIndex={0} className={rowShellCls} {...shared}>
      {children}
    </div>
  );
}

/** The table twin — every charted value is reachable without reading a bar. */
export function BarRankingTable({
  rows,
  measure,
}: {
  rows: BarRow[];
  measure: string;
  className?: string;
}) {
  const total = rows.reduce((n, r) => n + r.value, 0);
  return (
    <div className="overflow-x-auto">
      <table className="w-full border-collapse text-left text-base">
        <thead>
          <tr>
            {['', measure, 'Share'].map((h, i) => (
              <th
                key={h || i}
                className={cn(
                  'border-b-2 border-hairline pb-3 font-mono text-sm font-semibold tracking-wide text-ink-secondary uppercase',
                  i > 0 && 'text-right',
                )}
              >
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.key} className="border-b border-hairline/70">
              <td className="py-3 pr-4 font-semibold">{row.label}</td>
              <td className="py-3 pr-4 text-right tabular-nums">{row.display}</td>
              <td className="py-3 text-right text-ink-muted tabular-nums">
                {total ? Math.round((row.value / total) * 100) : 0}%
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
